import { useEffect, useRef } from 'react';

/* ==================================================================
   STREAK WIN SHEET — motion sequence
   big tick (aha) → FLIP into the day slot → card + progress + CTA
   ================================================================== */
const CAN_ANIMATE = !!(window.Element && Element.prototype.animate); /* old WebViews lack WAAPI */
const REDUCED = !CAN_ANIMATE ||
  !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

export default function WinSheet({ on, info, onCta }) {
  const artRef = useRef(null), tickRef = useRef(null), chkRef = useRef(null), lblRef = useRef(null),
    ringRef = useRef(null), cardRef = useRef(null), progRef = useRef(null), ctaRef = useRef(null),
    shineRef = useRef(null), tilesRef = useRef(null);

  useEffect(() => {
    if (!on || !info) return undefined;
    const art = artRef.current, tick = tickRef.current, chk = chkRef.current, lbl = lblRef.current,
      ring = ringRef.current, card = cardRef.current, prog = progRef.current, cta = ctaRef.current,
      shine = shineRef.current, tilesEl = tilesRef.current;
    const timers = [];

    /* reset */
    [tick, lbl, ring, card, prog, cta].forEach((el) => {
      if (el.getAnimations) el.getAnimations().forEach((a) => a.cancel());
    });
    tilesEl.querySelectorAll('.gfg-tile').forEach((t, i) => {
      t.classList.toggle('gfg-done', i < info.dayIndex);
      t.classList.remove('gfg-sweep');
    });
    tick.style.transform = 'scale(.55)'; tick.style.opacity = '0'; tick.style.display = 'grid';
    chk.style.strokeDashoffset = '52';
    lbl.style.opacity = '0'; lbl.style.transform = 'translateY(10px)';
    card.style.opacity = '0'; prog.style.opacity = '0'; cta.style.opacity = '0'; ring.style.opacity = '0';
    ring.style.width = ''; ring.style.height = ''; ring.style.marginLeft = ''; ring.style.left = ''; ring.style.top = '';

    const target = tilesEl.children[info.dayIndex];

    if (REDUCED) {
      tick.style.display = 'none'; lbl.style.opacity = '0';
      card.style.opacity = '1'; prog.style.opacity = '1'; cta.style.opacity = '1';
      if (target) target.classList.add('gfg-done');
      return undefined;
    }

    const E_OUT = 'cubic-bezier(.22,1,.36,1)';
    const E_SPRING = 'cubic-bezier(.34,1.46,.64,1)';

    /* 1 — the arch breathes in behind everything */
    art.animate([{ transform: 'scale(1.05)' }, { transform: 'scale(1)' }],
      { duration: 1600, easing: E_OUT, fill: 'forwards' });

    /* 2 — the tick arrives with a little overshoot */
    tick.animate([{ transform: 'scale(.55)', opacity: 0 }, { transform: 'scale(1.06)', opacity: 1, offset: .72 },
      { transform: 'scale(1)', opacity: 1 }],
      { duration: 620, delay: 240, easing: E_SPRING, fill: 'forwards' });

    /* 3 — the check draws itself */
    timers.push(setTimeout(() => {
      chk.animate([{ strokeDashoffset: 52 }, { strokeDashoffset: 0 }],
        { duration: 460, easing: 'cubic-bezier(.65,0,.35,1)', fill: 'forwards' });
      if (navigator.vibrate) navigator.vibrate([8, 30, 14]);
    }, 430));

    /* 4 — a single ring pushes out: the "landed" cue */
    timers.push(setTimeout(() => {
      ring.animate([{ transform: 'scale(1)', opacity: .55 }, { transform: 'scale(1.7)', opacity: 0 }],
        { duration: 760, easing: E_OUT });
    }, 600));

    /* 5 — the day label names what just happened */
    lbl.animate([{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }],
      { duration: 440, delay: 820, easing: E_OUT, fill: 'forwards' });

    /* 6 — THE HANDOFF: label clears, card rises, tick flies into its slot */
    timers.push(setTimeout(() => {
      lbl.animate([{ opacity: 1 }, { opacity: 0, transform: 'translateY(-6px)' }],
        { duration: 240, easing: 'ease-out', fill: 'forwards' });
      card.animate([{ opacity: 0, transform: 'translateY(26px)' }, { opacity: 1, transform: 'none' }],
        { duration: 560, easing: E_OUT, fill: 'forwards' });

      const a = tick.getBoundingClientRect(), b = target.getBoundingClientRect();
      const s = b.width / a.width;
      const dx = (b.left + b.width / 2) - (a.left + a.width / 2);
      const dy = (b.top + b.height / 2) - (a.top + a.height / 2);
      const fly = tick.animate(
        [{ transform: 'scale(1)', opacity: 1 },
          { transform: 'translate(' + (dx * .55) + 'px,' + (dy * .55 - 16) + 'px) scale(' + (1 - (1 - s) * .42) + ')', opacity: 1, offset: .55 },
          { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(' + s + ')', opacity: 1 }],
        { duration: 660, easing: 'cubic-bezier(.62,.02,.24,1)', fill: 'forwards' });

      fly.onfinish = () => {
        tick.style.display = 'none';
        target.classList.add('gfg-done'); /* real tile takes over */
        /* ring pulse at the slot */
        const r = target.getBoundingClientRect();
        ring.style.width = r.width + 'px'; ring.style.height = r.height + 'px';
        ring.style.marginLeft = '0'; ring.style.left = (target.offsetLeft + target.parentElement.offsetLeft) + 'px';
        ring.style.top = (target.offsetTop + target.parentElement.offsetTop) + 'px';
        ring.animate([{ transform: 'scale(1)', opacity: .7 }, { transform: 'scale(2.6)', opacity: 0 }],
          { duration: 700, easing: E_OUT });
        /* light runs across the rest of the row, left → right */
        [...tilesEl.querySelectorAll('.gfg-tile')].forEach((t, i) => {
          timers.push(setTimeout(() => {
            t.classList.add('gfg-sweep');
            timers.push(setTimeout(() => t.classList.remove('gfg-sweep'), 520));
          }, i * 52));
        });
        if (navigator.vibrate) navigator.vibrate(10);
        prog.animate([{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }],
          { duration: 420, delay: 180, easing: E_OUT, fill: 'forwards' });
        cta.animate([{ opacity: 0, transform: 'translateY(12px)' }, { opacity: 1, transform: 'none' }],
          { duration: 460, delay: 300, easing: E_OUT, fill: 'forwards' });
        timers.push(setTimeout(() => shine.animate(
          [{ transform: 'translateX(-120%)' }, { transform: 'translateX(120%)' }],
          { duration: 900, easing: 'ease-in-out' }), 820));
      };
    }, 1560));

    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, info]);

  const win = info ? info.window : 7;
  const dayIndex = info ? info.dayIndex : 0;
  const tiles = [];
  const labels = [];
  for (let i = 0; i < win; i++) {
    tiles.push(
      <div
        key={i}
        className={'gfg-tile' + (i < dayIndex ? ' gfg-done' : '') + (i === 7 && i >= dayIndex ? ' gfg-bonus' : '')}
      >
        <svg viewBox="0 0 24 24"><path d="M4 12.5l5 5L20 6.5" /></svg>
      </div>
    );
    labels.push(
      <div key={i} className={'gfg-daylbl' + (i <= dayIndex ? ' gfg-on' : '')}>Day {i + 1}</div>
    );
  }

  return (
    <div className={'gfg-sheet gfg-win' + (on ? ' gfg-on' : '')}>
      <div className="gfg-win-art" ref={artRef} />
      <div className="gfg-win-head">
        <h3 className="gfg-win-title">{info ? info.title : ''}</h3>
        <p className="gfg-win-sub">{info ? info.sub : ''}</p>
      </div>
      <div className="gfg-win-stage">
        <div className="gfg-win-ring" ref={ringRef} />
        <div className="gfg-bigtick" ref={tickRef}>
          <svg viewBox="0 0 48 48"><path ref={chkRef} d="M11 25.5l9.5 9.5L38 15" /></svg>
        </div>
        <div className="gfg-bigtick-lbl" ref={lblRef}>Day {dayIndex + 1}</div>
        <div className="gfg-card gfg-win-card" ref={cardRef}>
          <div className="gfg-tiles" ref={tilesRef}>{tiles}</div>
          <div className="gfg-daylbls">{labels}</div>
          <div className="gfg-win-prog" ref={progRef}>{info ? info.progress : ''}</div>
        </div>
      </div>
      <button className="gfg-cta" ref={ctaRef} onClick={onCta}>
        <span>{info ? info.ctaLabel : ''}</span>
        <svg viewBox="0 0 24 24"><path d="M4 12h15M13 6l6 6-6 6" /></svg>
        <span className="gfg-shine" ref={shineRef} />
      </button>
    </div>
  );
}
